import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import { connect } from "react-redux";
import { LuChevronDown, LuImagePlus, LuLoaderCircle, LuPlay, LuX } from "react-icons/lu";
import MotionScenePlayback from "../../../plugins/MotionScene/MotionScenePlayback";
import { getBrandKit } from "../../services/brandKitService";
import { isBuildLocked } from "../ai-chat/buildMode";
import { ID_PREFIX_BOX, ID_PREFIX_PAGE } from "../../../common/constants";
import { isFileTooLarge, fileTooLargeMessage } from "../../../common/upload_limits.es6";
import { isPage } from "../../../common/utils";
import { listLogoIntroCatalog, renderLogoIntro, uploadLogoFile } from "./logoIntroService";
import { insertLogoIntroScene } from "./insertLogoIntroScene";
import { insertLogoIntroWhenRendered } from "./logoIntroJobs";
import "./_logoIntroTab.scss";

export const TITLE_MAX = 80;
export const SUBTITLE_MAX = 120;
const DEFAULT_PRIMARY = "#7c3aed";
const DEFAULT_ACCENT = "#06b6d4";
// Text size as a percentage of the template's own size (server: 0.6–1.6).
export const TEXT_SIZE_MIN = 70;
export const TEXT_SIZE_MAX = 150;
// Shown in the colour input while the template picks the text colour itself.
const AUTO_TEXT_COLOR = "#1c2333";
export const OUTPUTS = ["motion", "mp4", "webp"];
export const PLACEMENTS = ["start", "after"];

const lang = () => (i18n.language || "en").slice(0, 2);
const templateName = (tpl) => (tpl.name && (tpl.name[lang()] || tpl.name.en)) || tpl.id;

// The main logo first, then the library's own order (same rule as the
// add-logo popup's library picker).
const logosOf = (kit) =>
  ((kit && kit.assets && kit.assets.logo) || [])
    .filter((a) => a && a.url)
    .sort((a, b) => Number(!!b.isPrimary) - Number(!!a.isPrimary));

const trainingIdOf = () =>
  window.idealStudioTrainingId || new URLSearchParams(window.location.search).get("id") || "";

/** One numbered card. Collapsible ones show `summary` in the header when closed. */
function Section({ n, title, collapsible, open, onToggle, summary, children }) {
  const isOpen = !collapsible || open;
  const header = (
    <>
      <span className="logo-intro__badge">{n}</span>
      <span className="logo-intro__section-title">{title}</span>
      {collapsible && !isOpen && summary && (
        <span className="logo-intro__section-summary">{summary}</span>
      )}
      {collapsible && (
        <LuChevronDown size={16} className={`logo-intro__chevron ${isOpen ? "is-open" : ""}`} />
      )}
    </>
  );
  return (
    <section className="logo-intro__section">
      {collapsible ? (
        <button
          type="button"
          className="logo-intro__section-head is-button"
          aria-expanded={isOpen}
          onClick={onToggle}
        >
          {header}
        </button>
      ) : (
        <div className="logo-intro__section-head">{header}</div>
      )}
      {isOpen && <div className="logo-intro__section-body">{children}</div>}
    </section>
  );
}

/** A compact segmented control (radio group). */
function Segment({ value, options, onChange, label }) {
  return (
    <div className="logo-intro__segment" role="radiogroup" aria-label={label}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={value === opt.value}
          className={value === opt.value ? "is-active" : ""}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Left rail → "Logo Intro" side panel (see LogoIntroPanel).
 *
 * Pick a template, fill it with the organization's logo and the course title,
 * audition it in place (the preview is rendered server-side but never stored),
 * then add it as a new scene — as a live motion scene, or rendered on a worker
 * to an mp4 / looping WebP. Everything happens inside the panel — no popup.
 */
export function LogoIntroTab({
  dispatch,
  navItemsIds,
  rootChildren,
  navItemsById,
  navItemSelected,
  viewToolbars,
  boxes,
  onAdded,
}) {
  // Scenes in course order for the "after scene…" picker (same rule as the
  // Screen Training import's picker: pages only, sections are not targets).
  const sceneIds = (navItemsIds || []).filter((id) => isPage(id));
  const [templates, setTemplates] = useState(null);
  const [templateId, setTemplateId] = useState(null);
  const [libraryLogos, setLibraryLogos] = useState(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [primary, setPrimary] = useState(DEFAULT_PRIMARY);
  const [accent, setAccent] = useState(DEFAULT_ACCENT);
  const [theme, setTheme] = useState("light");
  const [fonts, setFonts] = useState([]);
  const [fontFamily, setFontFamily] = useState("");
  const [textSize, setTextSize] = useState(100);
  const [bold, setBold] = useState(true);
  const [textColor, setTextColor] = useState(""); // "" → template decides
  const [uppercase, setUppercase] = useState(false);
  const [output, setOutput] = useState("motion");
  const [placement, setPlacement] = useState("start");
  const [afterNavId, setAfterNavId] = useState(() =>
    isPage(navItemSelected) ? navItemSelected : ""
  );
  const [styleOpen, setStyleOpen] = useState(false);
  const [lookOpen, setLookOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewDuration, setPreviewDuration] = useState(0);
  const [busy, setBusy] = useState(null); // null | "upload" | "preview" | "add" | "render"
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    let cancelled = false;
    listLogoIntroCatalog()
      .then(({ templates: items, fonts: fontList }) => {
        if (cancelled) return;
        setTemplates(items);
        setFonts(fontList);
        if (items.length) setTemplateId(items[0].id);
      })
      .catch(() => !cancelled && setError(i18n.t("LogoIntro.load_failed")));
    // The brand kit seeds the logo and colours; failing it just leaves the
    // defaults, the user can still upload a logo.
    getBrandKit()
      .then((kit) => {
        if (cancelled) return;
        const logos = logosOf(kit);
        setLibraryLogos(logos);
        if (logos.length) setLogoUrl((current) => current || logos[0].url);
        const colors = (kit && kit.colors && kit.colors[0]) || null;
        if (colors && colors.primary) setPrimary(colors.primary);
        if (colors && (colors.accent || colors.secondary)) {
          setAccent(colors.accent || colors.secondary);
        }
      })
      .catch(() => !cancelled && setLibraryLogos([]));
    return () => {
      cancelled = true;
      mounted.current = false;
    };
  }, []);

  // Any change to what the scene looks like makes the current preview stale.
  useEffect(() => {
    setPreviewHtml("");
  }, [
    templateId,
    logoUrl,
    title,
    subtitle,
    primary,
    accent,
    theme,
    fontFamily,
    textSize,
    bold,
    textColor,
    uppercase,
  ]);

  const payload = () => ({
    templateId,
    logoUrl: logoUrl || undefined,
    title: title.trim() || undefined,
    subtitle: subtitle.trim() || undefined,
    primaryColor: primary,
    accentColor: accent,
    theme,
    fontFamily: fontFamily || undefined,
    textScale: textSize / 100,
    bold,
    textColor: textColor || undefined,
    uppercase,
  });

  const canRender = !!templateId && (!!logoUrl || !!title.trim()) && !busy;

  async function handleFile(file) {
    if (!file) return;
    if (isFileTooLarge(file)) {
      setError(fileTooLargeMessage());
      return;
    }
    setBusy("upload");
    setError(null);
    try {
      setLogoUrl(await uploadLogoFile(file));
    } catch (e) {
      setError(i18n.t("LogoIntro.upload_failed"));
    } finally {
      setBusy(null);
    }
  }

  async function handlePreview() {
    setBusy("preview");
    setError(null);
    try {
      const result = await renderLogoIntro({ ...payload(), preview: true });
      setPreviewDuration(result.durationSec);
      setPreviewHtml(result.html);
    } catch (e) {
      setError((e && e.message) || i18n.t("LogoIntro.error"));
    } finally {
      setBusy(null);
    }
  }

  const insert = (result) => {
    insertLogoIntroScene(dispatch, result, {
      placement,
      navItemsIds,
      rootChildren,
      navItemsById,
      afterNavId: afterNavId || sceneIds[0],
      boxes,
      sceneName: i18n.t("LogoIntro.scene_name"),
    });
    if (onAdded) onAdded(result.navId);
  };

  async function handleAdd() {
    if (isBuildLocked()) return;
    setBusy(output === "motion" ? "add" : "render");
    setError(null);
    try {
      const now = Date.now();
      const result = await renderLogoIntro({
        ...payload(),
        output,
        trainingId: trainingIdOf(),
        navId: ID_PREFIX_PAGE + now,
        boxId: ID_PREFIX_BOX + now,
      });
      if (output === "motion") {
        insert(result);
        return;
      }
      // mp4 / webp render on a worker; the job also shows in the job tray.
      // The wait lives in the store (a thunk), not in this component: closing
      // the panel mid-render still inserts the intro when the job completes.
      const done = await dispatch(
        insertLogoIntroWhenRendered(result.jobId, result, {
          placement,
          afterNavId: afterNavId || sceneIds[0],
          sceneName: i18n.t("LogoIntro.scene_name"),
        })
      );
      if (mounted.current && onAdded) onAdded(done.navId);
    } catch (e) {
      if (!mounted.current) return;
      setError((e && e.message) || i18n.t("LogoIntro.error"));
      setBusy(null);
    }
  }

  const fontName = (fonts.find((f) => f.id === fontFamily) || {}).name;
  const addLabel =
    busy === "render"
      ? i18n.t(output === "webp" ? "LogoIntro.rendering_webp" : "LogoIntro.rendering")
      : busy === "add"
        ? i18n.t("LogoIntro.adding")
        : i18n.t("LogoIntro.add");

  return (
    <div className="logo-intro">
      <div className="logo-intro__sections">
        <Section n={1} title={i18n.t("LogoIntro.sec_template")}>
          <div
            className="logo-intro__templates"
            role="radiogroup"
            aria-label={i18n.t("LogoIntro.templates")}
          >
            {templates === null && !error && (
              <LuLoaderCircle className="logo-intro__spin" size={18} />
            )}
            {(templates || []).map((tpl) => (
              <button
                key={tpl.id}
                type="button"
                role="radio"
                aria-checked={tpl.id === templateId}
                className={`logo-intro__tpl ${tpl.id === templateId ? "is-active" : ""}`}
                title={i18n.t("LogoIntro.seconds", { n: tpl.durationSec })}
                onClick={() => setTemplateId(tpl.id)}
              >
                {templateName(tpl)}
              </button>
            ))}
          </div>
        </Section>

        <Section n={2} title={i18n.t("LogoIntro.sec_logo")}>
          <div className="logo-intro__logo-row">
            <div className="logo-intro__logo-current">
              {logoUrl ? (
                <>
                  <img src={logoUrl} alt="" draggable={false} />
                  <button
                    type="button"
                    className="logo-intro__icon-btn"
                    title={i18n.t("LogoIntro.remove")}
                    aria-label={i18n.t("LogoIntro.remove")}
                    onClick={() => setLogoUrl("")}
                  >
                    <LuX size={12} />
                  </button>
                </>
              ) : (
                <span className="logo-intro__muted">—</span>
              )}
            </div>
            <button
              type="button"
              className="logo-intro__btn"
              disabled={busy === "upload"}
              onClick={() => fileRef.current && fileRef.current.click()}
            >
              {busy === "upload" ? (
                <LuLoaderCircle className="logo-intro__spin" size={14} />
              ) : (
                <LuImagePlus size={14} />
              )}
              <span>{i18n.t("LogoIntro.upload")}</span>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              style={{ display: "none" }}
              onChange={(e) => {
                handleFile(e.target.files && e.target.files[0]);
                e.target.value = "";
              }}
            />
          </div>
          {libraryLogos && libraryLogos.length > 0 && (
            <div className="logo-intro__library" aria-label={i18n.t("LogoIntro.from_library")}>
              {libraryLogos.map((asset) => (
                <button
                  key={asset.id || asset.url}
                  type="button"
                  title={asset.name}
                  className={`logo-intro__library-item ${asset.url === logoUrl ? "is-active" : ""}`}
                  onClick={() => setLogoUrl(asset.url)}
                >
                  <img src={asset.url} alt={asset.name || ""} draggable={false} />
                </button>
              ))}
            </div>
          )}
        </Section>

        <Section n={3} title={i18n.t("LogoIntro.sec_text")}>
          <label className="logo-intro__field">
            <span className="logo-intro__label">
              {i18n.t("LogoIntro.title")}
              <span className="logo-intro__count">
                {title.length}/{TITLE_MAX}
              </span>
            </span>
            <input
              type="text"
              value={title}
              maxLength={TITLE_MAX}
              placeholder={i18n.t("LogoIntro.title_ph")}
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>
          <label className="logo-intro__field">
            <span className="logo-intro__label">
              {i18n.t("LogoIntro.subtitle")}
              <span className="logo-intro__count">
                {subtitle.length}/{SUBTITLE_MAX}
              </span>
            </span>
            <input
              type="text"
              value={subtitle}
              maxLength={SUBTITLE_MAX}
              placeholder={i18n.t("LogoIntro.subtitle_ph")}
              onChange={(e) => setSubtitle(e.target.value)}
            />
          </label>

          <button
            type="button"
            className="logo-intro__disclosure"
            aria-expanded={styleOpen}
            onClick={() => setStyleOpen((v) => !v)}
          >
            <span>{i18n.t("LogoIntro.text_style")}</span>
            {!styleOpen && (
              <span className="logo-intro__disclosure-summary">
                {fontName || i18n.t("LogoIntro.font_default")} · %{textSize}
              </span>
            )}
            <LuChevronDown
              size={16}
              className={`logo-intro__chevron ${styleOpen ? "is-open" : ""}`}
            />
          </button>
          {styleOpen && (
            <div className="logo-intro__style">
              <label className="logo-intro__field">
                <span className="logo-intro__label">{i18n.t("LogoIntro.font")}</span>
                <select value={fontFamily} onChange={(e) => setFontFamily(e.target.value)}>
                  <option value="">{i18n.t("LogoIntro.font_default")}</option>
                  {fonts.map((font) => (
                    <option key={font.id} value={font.id}>
                      {font.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="logo-intro__field">
                <span className="logo-intro__label">
                  {i18n.t("LogoIntro.size")}
                  <span className="logo-intro__count">%{textSize}</span>
                </span>
                <input
                  type="range"
                  min={TEXT_SIZE_MIN}
                  max={TEXT_SIZE_MAX}
                  step={5}
                  value={textSize}
                  onChange={(e) => setTextSize(Number(e.target.value))}
                />
              </label>
              <div className="logo-intro__grid logo-intro__grid--style">
                <div className="logo-intro__field">
                  <span className="logo-intro__label">
                    {i18n.t("LogoIntro.text_color")}
                    {textColor && (
                      <button
                        type="button"
                        className="logo-intro__link"
                        onClick={() => setTextColor("")}
                      >
                        {i18n.t("LogoIntro.text_color_auto")}
                      </button>
                    )}
                  </span>
                  <input
                    type="color"
                    aria-label={i18n.t("LogoIntro.text_color")}
                    value={textColor || AUTO_TEXT_COLOR}
                    onChange={(e) => setTextColor(e.target.value)}
                  />
                </div>
                <div className="logo-intro__field">
                  <span className="logo-intro__label">{i18n.t("LogoIntro.style")}</span>
                  <div className="logo-intro__segment">
                    <button
                      type="button"
                      aria-pressed={bold}
                      className={bold ? "is-active" : ""}
                      onClick={() => setBold((v) => !v)}
                    >
                      <b>{i18n.t("LogoIntro.bold")}</b>
                    </button>
                    <button
                      type="button"
                      aria-pressed={uppercase}
                      className={uppercase ? "is-active" : ""}
                      onClick={() => setUppercase((v) => !v)}
                    >
                      {i18n.t("LogoIntro.uppercase")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Section>

        <Section
          n={4}
          title={i18n.t("LogoIntro.sec_look")}
          collapsible
          open={lookOpen}
          onToggle={() => setLookOpen((v) => !v)}
          summary={
            <>
              <i className="logo-intro__swatch" style={{ background: primary }} />
              <i className="logo-intro__swatch" style={{ background: accent }} />
              {i18n.t(`LogoIntro.${theme}`)}
            </>
          }
        >
          <div className="logo-intro__grid logo-intro__grid--3">
            <label className="logo-intro__field">
              <span className="logo-intro__label">{i18n.t("LogoIntro.primary")}</span>
              <input type="color" value={primary} onChange={(e) => setPrimary(e.target.value)} />
            </label>
            <label className="logo-intro__field">
              <span className="logo-intro__label">{i18n.t("LogoIntro.accent")}</span>
              <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} />
            </label>
            <div className="logo-intro__field">
              <span className="logo-intro__label">{i18n.t("LogoIntro.theme")}</span>
              <Segment
                value={theme}
                onChange={setTheme}
                label={i18n.t("LogoIntro.theme")}
                options={["light", "dark"].map((v) => ({
                  value: v,
                  label: i18n.t(`LogoIntro.${v}`),
                }))}
              />
            </div>
          </div>
          <div className="logo-intro__hint">
            {theme === "dark" ? i18n.t("LogoIntro.dark_hint") : i18n.t("LogoIntro.look_hint")}
          </div>
        </Section>

        <Section n={5} title={i18n.t("LogoIntro.sec_output")}>
          <div className="logo-intro__field">
            <Segment
              value={output}
              onChange={setOutput}
              label={i18n.t("LogoIntro.sec_output")}
              options={OUTPUTS.map((v) => ({ value: v, label: i18n.t(`LogoIntro.output_${v}`) }))}
            />
            <span className="logo-intro__hint">{i18n.t(`LogoIntro.output_${output}_desc`)}</span>
          </div>
          <div className="logo-intro__field">
            <span className="logo-intro__label">{i18n.t("LogoIntro.place")}</span>
            <Segment
              value={placement}
              onChange={setPlacement}
              label={i18n.t("LogoIntro.place")}
              options={PLACEMENTS.map((v) => ({ value: v, label: i18n.t(`LogoIntro.place_${v}`) }))}
            />
            {placement === "after" && (
              <select
                aria-label={i18n.t("LogoIntro.place_scene")}
                value={afterNavId || sceneIds[0] || ""}
                onChange={(e) => setAfterNavId(e.target.value)}
              >
                {sceneIds.map((id, i) => {
                  const name =
                    (viewToolbars && viewToolbars[id] && viewToolbars[id].viewName) ||
                    i18n.t("slide");
                  return (
                    <option key={id} value={id}>
                      {i + 1}. {name}
                    </option>
                  );
                })}
              </select>
            )}
          </div>
        </Section>

        <Section n={6} title={i18n.t("LogoIntro.sec_preview")}>
          <div className="logo-intro__stage">
            {previewHtml ? (
              <MotionScenePlayback
                state={{ durationSec: previewDuration }}
                inlineHtml={previewHtml}
                visor
                visorPlaying
              />
            ) : (
              <button
                type="button"
                className="logo-intro__stage-empty"
                disabled={!canRender}
                onClick={handlePreview}
              >
                {busy === "preview" ? (
                  <LuLoaderCircle className="logo-intro__spin" size={22} />
                ) : (
                  <LuPlay size={22} />
                )}
                <span>
                  {busy === "preview"
                    ? i18n.t("LogoIntro.previewing")
                    : templateId
                      ? i18n.t("LogoIntro.preview")
                      : i18n.t("LogoIntro.preview_empty")}
                </span>
              </button>
            )}
          </div>
        </Section>
      </div>

      <div className="logo-intro__footer">
        {error && <div className="logo-intro__error">{error}</div>}
        {busy === "render" && (
          <div className="logo-intro__hint">{i18n.t("LogoIntro.render_wait_hint")}</div>
        )}
        <div className="logo-intro__actions">
          <button
            type="button"
            className="logo-intro__secondary"
            disabled={!canRender}
            onClick={handlePreview}
          >
            {busy === "preview" ? (
              <LuLoaderCircle className="logo-intro__spin" size={14} />
            ) : (
              <LuPlay size={14} />
            )}
            <span>{i18n.t("LogoIntro.preview")}</span>
          </button>
          <button
            type="button"
            className="logo-intro__primary"
            disabled={!canRender}
            onClick={handleAdd}
          >
            {(busy === "add" || busy === "render") && (
              <LuLoaderCircle className="logo-intro__spin" size={14} />
            )}
            <span>{addLabel}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

LogoIntroTab.propTypes = {
  dispatch: PropTypes.func.isRequired,
  navItemsIds: PropTypes.array,
  rootChildren: PropTypes.array,
  navItemsById: PropTypes.object,
  navItemSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  viewToolbars: PropTypes.object,
  boxes: PropTypes.object,
  /** Called with the new scene id once it is in the course. */
  onAdded: PropTypes.func,
};

function mapStateToProps(state) {
  const present = state && state.undoGroup && state.undoGroup.present;
  const byId = (present && present.navItemsById) || {};
  const root = byId[0];
  return {
    navItemsIds: (present && present.navItemsIds) || [],
    rootChildren: (root && root.children) || [],
    navItemsById: byId,
    navItemSelected: present && present.navItemSelected,
    viewToolbars: (present && present.viewToolbarsById) || {},
    boxes: (present && present.boxesById) || {},
  };
}

export default connect(mapStateToProps)(LogoIntroTab);
